import { isCancel } from 'axios';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAnalysisStore } from '../store/useAnalysisStore';
import { reportApi } from '../assets/api/apis';
import { ANALYSIS_STATUS } from '../data/define';

export function useStartVideoAnalysis() {
  const setAnalysisResult = useAnalysisStore((s) => s.setAnalysisResult);
  const setAiJobId = useAnalysisStore((s) => s.setAiJobId);
  const setLoadingProgress = useAnalysisStore((s) => s.setLoadingProgress);
  const queryClient = useQueryClient();

  const startAnalysis = useCallback(async (
    thumbnail: Blob | File | string,
    kidId: string | number,
    target: string,
    mi: number[],
    reportId?: string | number,
    type?: string,
    signal?: AbortSignal,
  ): Promise<number | null> => {
    const savedVideo = useAnalysisStore.getState().savedVideo;
    if (!savedVideo) return null;

    // 이전 분석의 잡 id·폴링 캐시가 남아 있으면 새 잡 id 를 받기 전까지 이전 결과가 화면에 뜬다(user-173·174·175)
    setAiJobId(null);
    setLoadingProgress(0);
    queryClient.removeQueries({ queryKey: ['aiJobStatus'] });

    setAnalysisResult({
      status: ANALYSIS_STATUS.PENDING,
      data: null,
      error: null,
      info: { kidId, mi, reportId, type },
    });

    try {
      const fd = new FormData();
      fd.append('video', savedVideo as Blob);
      fd.append('image', thumbnail as string);
      fd.append('kidId', String(kidId));
      fd.append('target', target);
      fd.append('mi', JSON.stringify(mi || []));
      if (reportId) {
        fd.append('reportId', String(reportId));
      }

      const res = await reportApi.postThumbnailVideoSet2(fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal,
      });

      const { aiJobId } = res.data;
      if (aiJobId) {
        setAiJobId(aiJobId);
        return aiJobId;
      }
      return null;
    } catch (e: any) {
      if (isCancel(e)) {
        console.log('❌ 요청 취소됨');
        return null;
      }

      if (e.response?.status === 422) {
        setAnalysisResult({
          status: ANALYSIS_STATUS.NOTHING,
          data: null,
          error: e,
          info: null,
        });
      } else {
        setAnalysisResult({
          status: ANALYSIS_STATUS.ERROR,
          data: null,
          error: e,
          info: null,
        });
      }
      return null;
    }
  }, [setAnalysisResult, setAiJobId, setLoadingProgress, queryClient]);

  return { startAnalysis };
}
